import type { JSX, ReactPortal } from 'react';
import { createPortal } from 'react-dom';
import { useRecoilValue } from 'recoil';

import { CARD_WIDTH } from 'constants/widths';

import { frozenCardDropAtom } from '../atoms';
import { FROZEN_DROP_PREVIEW_Z_INDEX } from '../frozenDrop.constants';

import { ActivityCardPragmatic } from './ActivityCardPragmatic/ActivityCardPragmatic';
import MultiSelectDragPreview from './MultiSelectDragPreview/MultiSelectDragPreview';

/**
 * Renders a frozen copy of the dropped card, snapped into the target cell,
 * while the plan/unassign call + refetch resolve. Replaces the vanished native
 * drag ghost so the user keeps seeing the card at its new spot during the load
 * state. Presentation only; the lifecycle (set on drop, cleared on settle) is
 * owned by `CardsViewPragmatic` / `RosterViewPragmatic`.
 *
 * Portaled to `document.body`, so it sits above the scroll containers but still
 * keeps the React context (Recoil / Redux / MUI / Router) that
 * `ActivityCardPragmatic` needs.
 */
const FrozenDropCardPragmatic = (): ReactPortal | JSX.Element | null => {
  'use memo';
  const frozenDrop = useRecoilValue(frozenCardDropAtom);

  if (!frozenDrop) return null;

  const {
    position,
    width,
    count,
    event,
    planningEntityId,
    planningEntityType
  } = frozenDrop;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: position.y,
        left: position.x,
        width: `${width || CARD_WIDTH}px`,
        // Sit just under the LoadingOverlay so the card shows through the
        // translucent overlay during the load state.
        zIndex: FROZEN_DROP_PREVIEW_Z_INDEX,
        pointerEvents: 'none',
        // Lift the frozen card off the list so it reads as the pending drop and
        // doesn't blend with the surrounding cards. drop-shadow follows the
        // card silhouette (incl. rounded corners); no colour change.
        filter: 'drop-shadow(0 3px 10px rgba(0, 0, 0, 0.35))'
      }}
    >
      {count > 1 ? (
        <MultiSelectDragPreview count={count} />
      ) : (
        <ActivityCardPragmatic
          {...event}
          planningEntityId={planningEntityId}
          planningEntityType={planningEntityType}
          selected={false}
          hovered={false}
        />
      )}
    </div>,
    document.body
  );
};

export default FrozenDropCardPragmatic;
